import { useCallback, useEffect, useState } from 'react';
import {
  Version,
  Zone,
} from '@helpers/types';
import { useHttp } from '@hooks/use-http';

export const useConfigureMap = () => {
  const http = useHttp();
  const [version, setVersion] = useState<Version | null>(null);
  const [zones, setZones] = useState<Array<Zone>>([]);
  const [loadingZones, setLoadingZones] = useState(false);
  const [forDeleteZone, setForDelete] = useState<Zone | null>(null);
  const [showDetailVersion, setShowDetailVersion] = useState(false);
  const [startCombine, setStartCombine] = useState(false);
  const [startUpload, setStartUpload] = useState(false);
  const onChangeVersion = useCallback((version: Version | null) => {
    setZones([]);
    setVersion(version);
  }, []);

  const loadZones = useCallback((version: Version) => {
    setLoadingZones(true);
    http.lastmile.getZones(version._id)
      .then((zones) => {
        setZones(zones);
      })
      .finally(() => {
        setLoadingZones(false);
      });
  }, [http]);

  useEffect(() => {
    if (!version) setZones([]);
    else loadZones(version);
  }, [version, loadZones]);

  const onDeleteZone = useCallback((version: Zone) => {
    setForDelete(version);
  }, []);
  const cancelDeleteZone = useCallback(() => {
    setForDelete(null);
  }, []);
  const confirmDeleteZone = useCallback(() => {
    setLoadingZones(true);
    http.lastmile.deleteZone(forDeleteZone!._id).finally(() => {
      setZones([]);
      setForDelete(null);
      loadZones(version!);
    });
  }, [forDeleteZone, version, http, loadZones]);

  const onToggleShowDetailVersion = useCallback(
    () => setShowDetailVersion(!showDetailVersion),
    [showDetailVersion],
  );

  const onCombine = useCallback(() => setStartCombine(true), []);
  const cancelCombine = useCallback(() => setStartCombine(false), []);
  const onConfirmCombine = useCallback(
    (zones: Array<Zone>, name: string) => {
      setZones([]);
      setLoadingZones(true);
      http.lastmile.disolveZones(
        zones.map((z) => z._id),
        name,
      )
        .then(() => {
          loadZones(version!);
        })
        .finally(() => {
          setStartCombine(false);
        });
    },
    [version, http, loadZones],
  );

  const onStartUpload = useCallback(() => {
    setStartUpload(true);
  }, []);
  const onCancelUpload = useCallback(() => {
    setStartUpload(false);
  }, []);

  return {
    zones,
    version,
    onCombine,
    startUpload,
    startCombine,
    loadingZones,
    onDeleteZone,
    forDeleteZone,
    onStartUpload,
    cancelCombine,
    onCancelUpload,
    onChangeVersion,
    cancelDeleteZone,
    onConfirmCombine,
    showDetailVersion,
    confirmDeleteZone,
    onToggleShowDetailVersion,
  };
};